<template>
  <div v-if="showTable" class="section-container mt-2">
    <NewGenericBTable
      :display_id="'SectionTable'"
      :key="tableKey"
      :search_filter="true"
      :columns_display="true"
      :fields="fields"
      :url="'/teaching/alt-section/'"
      :pre_filter="preFilter"
      :show_pagination="true"
      :pagination="10"
      @current_tag="handleCurrentTag"
    >
      <template #btn-download>
        <!-- Un <button disabled> no dispara mouseenter/mouseleave, así que el
        tooltip de GenericButtonDownload no se ve mientras el botón está
        deshabilitado. Se agrega este <span> exclusivamente para ese caso: el
        tooltip de "faltan filtros" vive acá, y el de GenericButtonDownload
        (con el botón habilitado) sigue funcionando como siempre. -->
        <span
          class="excel-download-wrapper ml-1 mr-2"
          tabindex="0"
          v-b-tooltip.topleft.v-secondary.noninteractive="
            !canDownloadExcel
              ? $t('section.sectionView.excelRequiresFilters')
              : ''
          "
        >
          <GenericButtonDownload
            :tooltip_text="
              canDownloadExcel
                ? `${$getVisibleNames('teaching.section', true, 'Secciones')} XLSX`
                : ''
            "
            :click_button="downloadExcel"
            :disabled_button="disabled_button_excel || !canDownloadExcel"
            :icon_excel="true"
          ></GenericButtonDownload>
        </span>
      </template>
      <template v-slot:cell(matter)="row">
        <span
          class="current-sections-item"
          v-if="row.item.egress_profile_matter != null"
        >
          <b-link :id="`popover-button-variant-${row.item.id}`">
            {{ getMatterName(row.item) }}
          </b-link>
          <b-popover
            :target="`popover-button-variant-${row.item.id}`"
            triggers="focus"
          >
            <template #title>{{
              $t("section.sectionView.navigation")
            }}</template>
            <div>
              <template>
                <div class="profiles-container">
                  <b-button
                    variant="none"
                    :to="{
                      name: 'MatterProgramView',
                      params: {
                        matter_id: row.item.egress_profile_matter,
                      },
                    }"
                    class="profiles mb-1 text-white"
                  >
                    {{
                      $t("reusable.common.goToTooltip", {
                        entity: $getVisibleNames(
                          "manual.matter_program",
                          false,
                          "Programa de la Asignatura"
                        ),
                      })
                    }}.
                  </b-button>
                </div>
              </template>
            </div>
          </b-popover>
        </span>
        <span v-else class="current-sections-item">
          <b-link :id="`popover-button-variant-matter-${row.item.id}`">
            {{ getMatterName(row.item) }}
          </b-link>
          <b-popover
            :target="`popover-button-variant-matter-${row.item.id}`"
            triggers="focus"
          >
            <template #title>{{
              $t("section.sectionView.navigation")
            }}</template>
            <div>
              <template>
                <div class="profiles-container">
                  <b-button
                    variant="none"
                    :to="{
                      name: 'OldMatterProgramView',
                      params: {
                        matter_id: row.item.matter,
                      },
                    }"
                    class="profiles mb-1 text-white"
                  >
                    {{
                      $t("reusable.common.goToTooltip", {
                        entity: $getVisibleNames(
                          "manual.matter_program",
                          false,
                          "Programa de la Asignatura"
                        ),
                      })
                    }}.
                  </b-button>
                </div>
              </template>
            </div>
          </b-popover>
        </span>
      </template>
      <template v-slot:cell(egress_profile)="row">
        {{ getEgressProfileName(row.item) }}
      </template>
      <template v-slot:cell(name)="row">
        <span class="current-sections-item">
          <b-link
            :title="
              $t('reusable.common.viewTooltip', {
                entity: $getVisibleNames(
                  'teaching.section',
                  false,
                  'Sección'
                ),
              })
            "
            @click="$emit('launch-modal-update', row.item)"
            >{{ row.item.name }}</b-link
          >
        </span>
      </template>
      <template v-slot:cell(shift)="row">
        <b-badge class="pill-badge" variant="info" pill>
          {{ shiftName(row.item.shift) }}
        </b-badge>
      </template>
      <template v-slot:cell(period)="row">
        {{ periodName(row.item.period) }}
      </template>
      <template v-slot:cell(professors)="row">
        <!-- Los docentes vienen anidados en la sección con ?expand=professors -->
        <template v-for="professor in row.item.professors_detail || []">
          <div :key="row.item.id + '-' + professor.id">
            <b-badge
              v-b-tooltip.v-secondary.noninteractive="professor.email"
              style="cursor: help"
              variant="info"
              pill
            >
              {{ professor.first_name }}
              {{ professor.last_name }}
            </b-badge>
          </div>
        </template>
      </template>
      <template v-slot:cell(action)="row">
        <div class="d-flex justify-content-end">
          <!-- Badge con cantidad de estudiantes -->
          <div
            class="d-inline-flex align-items-center"
            @mouseenter="fetchStudentsForSection(row.item)"
          >
            <b-badge
              :id="`students-badge-${row.item.id}`"
              variant="secondary"
              pill
              style="cursor: help; min-width: 2.5em; text-align: center"
              class="mr-1"
            >
              {{ row.item.students ? row.item.students.length : 0 }}
            </b-badge>
            <b-popover
              :target="`students-badge-${row.item.id}`"
              triggers="hover"
              placement="left"
            >
              <template #title>
                {{ $t("section.sectionView.studentsList") }}
                ({{ row.item.students ? row.item.students.length : 0 }})
              </template>
              <div
                v-if="loading_students_section_id === row.item.id"
                class="text-center py-2"
              >
                <b-spinner small variant="primary"></b-spinner>
                <span class="ml-2">{{ $t("section.sectionView.loading") }}</span>
              </div>
              <div
                v-else-if="row.item.students && row.item.students.length > 0"
                class="students-popover-list"
              >
                <div
                  v-for="(student, index) in getSortedStudents(row.item.id)"
                  :key="student.id"
                  class="students-popover-item"
                >
                  <span class="students-popover-index">{{ index + 1 }}.</span>
                  <span
                    >{{ student.first_name }} {{ student.last_name }}</span
                  >
                </div>
              </div>
              <div v-else class="text-muted">
                {{
                  $t("section.sectionView.noStudentsAssigned", {
                    entity: $getVisibleNames(
                      "teaching.section",
                      false,
                      "Sección"
                    ),
                  })
                }}
              </div>
            </b-popover>
          </div>
          <div v-if="!allows_crud">
            <b-button
              size="sm"
              variant="none"
              @click="$emit('launch-modal-update', row.item)"
              v-b-tooltip.topleft.v-secondary.noninteractive="
                $t('section.sectionView.viewSectionDetails', {
                  entity: $getVisibleNames(
                    'teaching.section',
                    false,
                    'Sección'
                  ),
                })
              "
              ><b-icon icon="eye" scale="1"></b-icon
            ></b-button>
          </div>
          <b-button
            v-if="
              user &&
              (user.group_names.includes(
                'DIRECTIVOS ACADÉMICOS INSTITUCIONALES'
              ) ||
                user.is_superuser) &&
              allows_crud &&
              row.item &&
              institution &&
              ((institution.current_periods != null &&
                !institution.current_periods
                  .map((x) => x.id)
                  .includes(row.item.period)) ||
                institution.current_periods == null ||
                !row.item.is_active)
            "
            size="sm"
            variant="none"
            class="pl-0"
            :class="{ 'text-danger': row.item.is_active }"
            v-b-tooltip.v-secondary.noninteractive="
              $t('section.sectionView.toggleSectionStatus', {
                action: row.item.is_active
                  ? $t('reusable.common.close')
                  : $t('reusable.common.open'),
                entity: $getVisibleNames(
                  'teaching.section',
                  false,
                  'Sección'
                ),
              })
            "
            @click="changeStateSection(row.item)"
          >
            <template v-if="!row.item.is_active">
              <b-icon icon="lock-fill" scale="1"></b-icon>
            </template>
            <template v-else>
              <b-icon icon="unlock-fill" scale="1"></b-icon>
            </template>
          </b-button>
          <!-- Acceso rápido a los reportes de GeneralReport con los filtros de
          la sección preseleccionados (mismo componente que el dashboard
          directivo). Solo cuando la sección pertenece a un perfil de egreso. -->
          <div
            v-if="
              row.item.egress_profile_matter_detail &&
              institution &&
              institution.show_kl_reports
            "
            class="d-inline-flex align-items-center mr-1"
          >
            <SectionReportsNav
              :egress_profile_id="
                row.item.egress_profile_matter_detail.egress_profile_id
              "
              :egress_profile_matter_id="row.item.egress_profile_matter"
              :section_id="row.item.id"
              :plan_level="row.item.egress_profile_matter_detail.plan_level"
              :period_id="row.item.period"
            ></SectionReportsNav>
          </div>
          <div>
            <ButtonWrapper :item_id="row.item.id" class="mr-1">
              <template v-slot:btn-slot>
                <div class="container-btn-three-dots">
                  <b-button
                    variant="secondary"
                    @click="redirectToSection(row.item.id)"
                    @contextmenu.prevent="
                      handleRightClickSection(row.item.id)
                    "
                    class="btn-popover-my-classes mb-2 w-100"
                  >
                    {{
                      $t("section.sectionView.sectionNotes", {
                        entity: $getVisibleNames(
                          "teaching.section",
                          false,
                          "Sección"
                        ),
                      })
                    }}
                  </b-button>
                  <b-button
                    v-if="institution && institution.show_kl_reports"
                    variant="secondary"
                    class="btn-popover-my-classes mb-2 w-100"
                    @click="redirectToAssessmentStudent(row.item.id)"
                    @contextmenu.prevent="
                      handleRightClickAssessmentStudent(row.item.id)
                    "
                    >{{ $t("section.sectionView.assessmentByStudent") }}
                  </b-button>
                  <b-button
                    variant="secondary"
                    @click="redirectToEvaluationTeacher(row.item.id)"
                    @contextmenu.prevent="
                      handleRightClickEvaluationTeacher(row.item.id)
                    "
                    class="btn-popover-my-classes mb-2 w-100"
                  >
                    {{
                      $t("section.sectionView.sectionEvaluations", {
                        evaluations: $getVisibleNames(
                          "evaluations2.evaluation",
                          true,
                          "Evaluaciones"
                        ),
                        entity: $getVisibleNames(
                          "teaching.section",
                          false,
                          "Sección"
                        ),
                      })
                    }}
                  </b-button>
                  <b-button
                    v-if="
                      institution &&
                      institution.kl_assessment &&
                      institution.show_kl_reports
                    "
                    variant="secondary"
                    @click="redirectToAssessmentRange(row.item.id)"
                    @contextmenu.prevent="
                      handleRightClickAssessmentRange(row.item.id)
                    "
                    class="btn-popover-my-classes w-100"
                  >
                    {{ $t("section.sectionView.assessmentReport") }}
                  </b-button>
                  <b-button
                    variant="secondary"
                    @click="redirectToProgressionEvaluation(row.item.id)"
                    @contextmenu.prevent="
                      handleRightClickProgressionEvaluation(row.item.id)
                    "
                    class="btn-popover-my-classes mt-2 w-100"
                  >
                    {{
                      $t("section.sectionView.evaluationsProgression", {
                        entity: $getVisibleNames(
                          "evaluations2.evaluation",
                          true,
                          "Evaluaciones"
                        ),
                      })
                    }}
                  </b-button>
                </div>
              </template>
            </ButtonWrapper>
          </div>
          <ButtonSmall
            v-if="allows_crud"
            v-can="'teaching.change_section'"
            :click_button="() => $emit('launch-modal-update', row.item)"
            :icon="'square'"
            :custom_class="`btn-cruds mt-1`"
            :tooltip_text="``"
          ></ButtonSmall>
          <ButtonSmall
            v-if="allows_crud"
            v-can="'teaching.delete_section'"
            :click_button="() => $emit('ask-delete', row.item)"
            :icon="'trash'"
            :custom_class="`btn-cruds mt-1`"
            :tooltip_text="``"
          ></ButtonSmall>
        </div>
      </template>
      <template #empty>
        <div class="text-center p-3">
          {{ $t("reusable.common.noMatchingData") }}
        </div>
      </template>
      <template #emptyfiltered>
        <div class="text-center p-3">
          {{ $t("reusable.common.noMatchingData") }}
        </div>
      </template>
    </NewGenericBTable>
  </div>
</template>

<script>
import * as names from "@/store/names";
import { mapGetters } from "vuex";
import {
  toast,
  showLoadingAlert,
  downloadFileFromResponse,
} from "@/utils/utils";
export default {
  name: "SectionTable",
  components: {
    NewGenericBTable: () => import("@/components/reusable/NewGenericBTable"),
    ButtonWrapper: () => import("@/components/reusable/ButtonWrapper"),
    ButtonSmall: () =>
      import("@/components/reusable/Buttons/Small/ButtonSmall"),
    GenericButtonDownload: () =>
      import("@/components/reusable/Buttons/GenericButtonDownload"),
    SectionReportsNav: () => import("@/components/reports/SectionReportsNav"),
  },
  props: {
    allows_crud: {
      type: Boolean,
      default: false,
    },
    selected_career_id: {
      type: Number,
      default: null,
    },
    selected_egress_profile_id: {
      type: Number,
      default: null,
    },
    selected_period_id: {
      type: Number,
      default: null,
    },
    selected_shift_id: {
      type: Number,
      default: null,
    },
    selected_campus_id: {
      type: Number,
      default: null,
    },
    selected_level: {
      type: Number,
      default: null,
    },
    selected_matter_id: {
      type: Number,
      default: null,
    },
    selected_section_view: {
      type: Number,
      default: 1,
    },
    // null = todas, true = solo activas, false = solo no activas.
    selected_is_active: {
      type: Boolean,
      default: null,
    },
    fields: {
      type: Array,
      required: true,
    },
    reload_key: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      loading_students_section_id: null,
      fetched_students_section_ids: new Set(),
      disabled_button_excel: false,
      // Términos activos del buscador de NewGenericBTable (@current_tag): se
      // incluyen en la descarga de Excel para que coincida con lo que la
      // tabla muestra reducido por texto, no solo por los filtros de arriba.
      search_tags: [],
    };
  },
  computed: {
    ...mapGetters({
      user: "getUser",
      institution: "getInstitution",
      sections: names.SECTIONS,
      egress_profiles_matters: names.EGRESS_PROFILES_MATTERS,
      matters: names.MATTERS,
      periods: names.PERIODS,
      shifts: names.SHIFTS,
    }),
    showTable() {
      // Se muestra siempre: al entrar a la vista, sin filtro, se listan
      // todas las secciones; los filtros solo acotan el resultado.
      return true;
    },
    preFilter() {
      let params = [];
      if (this.selected_section_view == 1) {
        if (this.selected_egress_profile_id)
          params.push(
            `egress_profile=${this.selected_egress_profile_id}`
          );
        if (this.selected_level)
          params.push(`plan_level=${this.selected_level}`);
      } else {
        if (this.selected_matter_id)
          params.push(`matter=${this.selected_matter_id}`);
      }
      if (this.selected_shift_id)
        params.push(`shift=${this.selected_shift_id}`);
      if (this.selected_campus_id)
        params.push(`campus=${this.selected_campus_id}`);
      if (this.selected_period_id)
        params.push(`period=${this.selected_period_id}`);
      // Filtro por estado: solo se agrega cuando el usuario eligió activas/no
      // activas; con "Todas" (null) no se envía para no acotar el resultado.
      // Se envía 1/0 (no true/false): el backend filtra directo sobre el ORM y
      // el BooleanField no acepta la cadena "true"/"false" en minúscula.
      if (this.selected_is_active != null)
        params.push(`is_active=${this.selected_is_active ? 1 : 0}`);
      // Trae docentes, asignatura y perfil de egreso ya resueltos (nombres incluidos):
      // evita ir al store/otros endpoints por cada fila, clave cuando se listan
      // Secciones de varios Perfiles de Egreso a la vez (sin filtro aplicado).
      params.push("expand=professors,egress_profile_matter,matter");
      return params.join("&");
    },
    tableKey() {
      return `section-table-${this.preFilter}-${this.reload_key}`;
    },
    // El backend rechaza el Excel sin estos filtros (evita barrer todas las
    // Secciones de la Institución en una sola descarga): se refleja acá para
    // deshabilitar el botón antes de siquiera intentar la descarga.
    canDownloadExcel() {
      return !!this.selected_egress_profile_id && !!this.selected_period_id;
    },
  },
  methods: {
    // Descarga a Excel las Secciones con los mismos filtros que están
    // aplicados en la tabla al momento de hacer clic.
    downloadExcel() {
      if (this.disabled_button_excel || !this.canDownloadExcel) return;
      this.disabled_button_excel = true;
      showLoadingAlert(
        this.$t("reusable.common.generatingExcel"),
        this.$t("reusable.common.pleaseWaitFile")
      );
      const payload = {};
      if (this.selected_egress_profile_id)
        payload.egress_profile = this.selected_egress_profile_id;
      if (this.selected_matter_id) payload.matter = this.selected_matter_id;
      if (this.selected_period_id) payload.period = this.selected_period_id;
      if (this.selected_campus_id) payload.campus = this.selected_campus_id;
      if (this.selected_shift_id) payload.shift = this.selected_shift_id;
      if (this.selected_level) payload.plan_level = this.selected_level;
      if (this.search_tags.length > 0)
        payload.search = this.search_tags.join(",");
      this.$restful
        .Post(`/teaching/excel_report_sections/`, payload, {
          responseType: "blob",
        })
        .then((response) => {
          this.$swal.close();
          downloadFileFromResponse(
            response,
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            () => {
              this.disabled_button_excel = false;
            },
            () => {
              this.disabled_button_excel = false;
              this.$swal.fire(
                this.$t("reusable.common.error"),
                this.$t("reusable.common.downloadError"),
                "error"
              );
            }
          );
        })
        .catch(() => {
          this.disabled_button_excel = false;
          this.$swal.close();
          this.$swal.fire(
            this.$t("reusable.common.error"),
            this.$t("reusable.common.problemGenerating"),
            "error"
          );
        });
    },
    fetchStudentsForSection(section) {
      if (
        !section.students ||
        section.students.length === 0 ||
        this.fetched_students_section_ids.has(section.id)
      )
        return;
      this.fetched_students_section_ids.add(section.id);
      this.loading_students_section_id = section.id;
      // Los estudiantes se piden anidados y solo al abrir el detalle de la sección
      this.$store
        .dispatch(names.FETCH_SECTION, {
          section_id: section.id,
          expand: ["students"],
        })
        .finally(() => {
          this.loading_students_section_id = null;
        });
    },
    getSortedStudents(section_id) {
      const section = this.sections.find((x) => x.id == section_id);
      if (!section || !section.students_detail) return [];
      // Se copia antes de ordenar: students_detail es una referencia al store
      return [...section.students_detail].sort((a, b) => {
        const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
        const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
      });
    },
    // El endpoint trae egress_profile_matter_detail/matter_detail (?expand=...) con el
    // nombre ya resuelto: evita depender de que el store tenga cargado ese Perfil de
    // Egreso (antes solo pasaba si el usuario ya lo había filtrado).
    getMatterName(item) {
      if (item.egress_profile_matter_detail && item.egress_profile_matter_detail.matter)
        return item.egress_profile_matter_detail.matter.name;
      if (item.matter_detail) return item.matter_detail.name;
      // Fallback por si el expand aún no llegó (ej. mientras carga la fila).
      if (item.egress_profile_matter) {
        const egress_profile_matter = this.egress_profiles_matters.find(
          (x) => x.id == item.egress_profile_matter
        );
        if (egress_profile_matter) {
          const matter = this.matters.find(
            (x) => x.id == egress_profile_matter.matter
          );
          return matter ? matter.name : "";
        }
        return "";
      }
      if (item.matter) {
        const matter = this.matters.find((x) => x.id == item.matter);
        return matter ? matter.name : "";
      }
      return "";
    },
    // Nombre del Perfil de Egreso de la fila: útil para identificar Secciones
    // cuando la tabla lista varios Perfiles de Egreso a la vez (sin filtro aplicado).
    getEgressProfileName(item) {
      if (item.egress_profile_matter_detail)
        return item.egress_profile_matter_detail.egress_profile_name || "-";
      return "-";
    },
    shiftName(shift_id) {
      const shift = this.shifts.find((x) => x.id == shift_id);
      return shift ? shift.name : "";
    },
    periodName(id) {
      const period = this.periods.find((x) => x.id == id);
      return period ? period.name : "";
    },
    changeStateSection(section) {
      if (!isNaN(section.id))
        this.$store
          .dispatch(names.PATCH_SECTION, {
            section_id: section.id,
            item: { is_active: !section.is_active },
          })
          .then((response) => {
            // La tabla mantiene sus propios items desde el endpoint, así que hay
            // que remontarla para que la fila refleje el nuevo is_active.
            this.$emit("reload-table");
            toast(
              this.$t("section.sectionView.sectionStatus", {
                entity: this.$getVisibleNames(
                  "teaching.section",
                  false,
                  "Sección"
                ),
                status: response.is_active
                  ? this.$t("section.sectionView.active")
                  : this.$t("section.sectionView.closed"),
              })
            );
          });
    },
    redirectToSection(id) {
      this.$router.push({
        name: "NewFullGradingView",
        params: { section_id: id },
      });
    },
    handleRightClickSection(id) {
      const url = this.$router.resolve({
        name: "NewFullGradingView",
        params: { section_id: id },
      }).href;
      window.open(url, "_blank");
    },
    redirectToEvaluationTeacher(id) {
      this.$router.push({
        name: "EvaluatorEvaluationsListView",
        params: { section_id: id },
      });
    },
    handleRightClickEvaluationTeacher(id) {
      const url = this.$router.resolve({
        name: "EvaluatorEvaluationsListView",
        params: { section_id: id },
      }).href;
      window.open(url, "_blank");
    },
    redirectToAssessmentRange(id) {
      this.$router.push({
        name: "AssessmentRangeSectionView",
        params: { section_id: id },
      });
    },
    handleRightClickAssessmentRange(id) {
      const url = this.$router.resolve({
        name: "AssessmentRangeSectionView",
        params: { section_id: id },
      }).href;
      window.open(url, "_blank");
    },
    redirectToProgressionEvaluation(id) {
      this.$router.push({
        name: "ProfessorProgressionReportView",
        params: { section_id: id },
      });
    },
    handleRightClickProgressionEvaluation(id) {
      const url = this.$router.resolve({
        name: "ProfessorProgressionReportView",
        params: { section_id: id },
      }).href;
      window.open(url, "_blank");
    },
    redirectToAssessmentStudent(id) {
      this.$router.push({
        name: "AssessmentStudentsView",
        params: { section_id: id },
      });
    },
    handleRightClickAssessmentStudent(id) {
      const url = this.$router.resolve({
        name: "AssessmentStudentsView",
        params: { section_id: id },
      }).href;
      window.open(url, "_blank");
    },
    handleCurrentTag(tags) {
      this.search_tags = tags;
    },
  },
  watch: {
    // Al cambiar un filtro de arriba, NewGenericBTable se remonta (cambia
    // :key) y pierde sus términos de búsqueda: se limpian acá para no
    // arrastrar tags de una tabla que ya no existe a la descarga de Excel.
    tableKey() {
      this.search_tags = [];
    },
  },
};
</script>

<style scoped>
/* El <span> que envuelve GenericButtonDownload (ver comentario junto a él en
   el template) queda fuera del stretch que la fila de botones aplicaba antes
   directo sobre el <b-button>: sin esto, el botón de Excel se ve más chico
   que el de columnas (VisibleColumns), que sí llega a la altura de la fila. */
.excel-download-wrapper {
  display: flex;
  align-items: stretch;
}
.section-container {
  padding: 1rem !important;
  margin-inline: 0.2rem;
  background: #eeeeee;
  border-radius: 10px;
}
.container-btn-three-dots {
  max-width: 250px;
}
.btn-popover-my-classes {
  padding: 0.1rem 0.3rem;
  font-size: 11pt;
  border-radius: 4px;
}
.pill-badge {
  font-size: 13px;
}
.current-sections-item:hover {
  background: rgb(208, 208, 208);
  border-radius: 8px;
}
.profiles-container {
  width: 100%;
}
.profiles {
  background-color: var(--secondary-color);
  border-radius: 4px;
  padding: 0 2em;
  width: 100%;
  font-size: 11pt;
  text-align: center;
}
.students-popover-list {
  max-height: 200px;
  overflow-y: auto;
}
.students-popover-item {
  display: flex;
  align-items: baseline;
  gap: 6px;
  padding: 2px 3px 2px 0px;
  border-radius: 4px;
  font-size: 12px;
}
.students-popover-item:not(:last-child) {
  border-bottom: 1px solid #f0f0f0;
}
.students-popover-index {
  font-size: 11px;
  flex-shrink: 0;
}
</style>
